import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import './RefundCancellation.css';
import Footer from '../Home/Footer';

const RefundCancellation = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Refund and Cancellation Policy | AI4Bazaar";
    
    if (isInView) {
      controls.start("visible");
    }

    const handlePopState = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.5
      }
    },
    hover: {
      scale: 1.01,
      transition: { duration: 0.2 }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8 }
    }
  };

  const sections = [
    {
      title: "Refund Policy",
      content: [
        "Our refund policy is designed to ensure that customers who are dissatisfied with our services or products can request a refund within a reasonable timeframe. We aim to build trust with all businesses that choose to work with us and provide them with the necessary support to make their digital transformation successful."
      ],
      subsections: [
        {
          title: "Website Development Services",
          content: [
            "Customers can request a full refund within 14 days of the website deployment if no additional services such as e-commerce integration or digital marketing solutions have been used. If additional services were utilized, a partial refund will be processed based on the value of services rendered. Refunds will be processed back to the original payment method used during the transaction."
          ]
        },
        {
          title: "E-commerce and Digital Marketing Solutions",
          content: [
            "If the service has not been initiated within 30 days of purchase, customers can request a full refund. If services such as e-commerce setup or digital marketing campaigns have already been initiated, refunds will be prorated based on the work completed at the time of the cancellation. Digital marketing campaigns once started are non-refundable."
          ]
        },
        {
          title: "Non-Refundable Items",
          content: [
            "Any administrative, setup, or integration fees that are non-refundable due to the nature of the work completed will be deducted from any refunds. If any service is fully rendered or accessed (e.g., marketing campaigns, data insights), it will not be eligible for a refund."
          ]
        },
        {
          title: "Refund Process",
          content: [
            "To request a refund, please contact our support team at support@ai4bazaar.com within the eligible refund period. The request must include details of the purchase, the reason for the refund, and any supporting documentation. Once the refund request is received, we will process it within 7 business days, and the refund will be issued through the original payment method."
          ]
        },
        {
          title: "Chargebacks and Disputes",
          content: [
            "In the case of a chargeback or payment dispute initiated through Razorpay, we will work closely with Razorpay to resolve the issue. All disputes will be handled according to our standard procedures, which may include providing transaction records or other documentation as needed. Please note that any service that has been completed or delivered is not eligible for chargeback claims."
          ]
        }
      ]
    },
    {
      title: "Cancellation Policy",
      content: [],
      subsections: [
        {
          title: "Website Development Cancellation",
          content: [
            "If you wish to cancel your website development service before deployment, a full refund will be provided. If the website is deployed and operational for less than 14 days, a partial refund will be issued, deducting any costs for work already completed. To cancel, you must notify us via email within 7 days of purchase."
          ]
        },
        {
          title: "E-commerce and Digital Marketing Services",
          content: [
            "For e-commerce and digital marketing subscriptions, cancellations can be requested within the first 30 days. If services have already been rendered, a prorated refund will be issued based on the value of services completed at the time of cancellation. Cancellations beyond the 30-day period will not be refunded."
          ]
        },
        {
          title: "Subscription-Based Services",
          content: [
            "For subscription-based services, customers can cancel at any time before the next billing cycle. If the service is canceled before the renewal date, no further charges will be applied."
          ]
        }
      ]
    },
    {
      title: "Mode of Refund",
      content: [
        "Refunds will be processed back to the original payment method used during the transaction (credit card, Razorpay wallet, etc.), subject to Razorpay's policies."
      ]
    },
    {
      title: "Final Decision",
      content: [
        "AI4Bazaar reserves the right to review all refund and cancellation requests on a case-by-case basis and to make a final decision in line with our policies. All decisions are final, but we aim to provide fair solutions to our customers."
      ]
    },
    {
      title: "Contact Us",
      content: [
        "If you have any questions or need further clarification regarding this policy, please contact our customer support team at support@ai4bazaar.com."
      ]
    }
  ];

  return (
    <div className="legal-document">
      <motion.div 
        ref={containerRef}
        className="terms-container"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.header 
          className="legal-header"
          variants={fadeInVariants}
          whileHover={{ scale: 1.02 }}
        >
          <motion.h1 
  variants={itemVariants}
  whileHover={{ color: "#2c5282" }}
  style={{ color: '#2c5282', textAlign: 'center' }}
>
  Refund and Cancellation Policy for AI
  <span style={{ position: 'relative', top: '-5px' }}>4</span>
  Bazaar
</motion.h1>

          <motion.p 
            className="effective-date"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </motion.header>

        <motion.div 
          className="intro-section"
          variants={fadeInVariants}
        >
          <motion.p variants={itemVariants}>
            At AI<span style={{ position: 'relative', top: '-3px' }}>4</span>Bazaar, we aim to provide a seamless digital transformation for small and local businesses. Our goal is to make the process of adopting digital solutions as smooth as possible. However, we understand that sometimes circumstances change, and you may need to cancel or request a refund. Below are the details of our refund and cancellation policy, which complies with industry standards and Razorpay's requirements.
          </motion.p>
        </motion.div>

        <motion.div 
          className="legal-content" 
          variants={containerVariants}
        >
          {sections.map((section, index) => (
            <motion.article 
              key={index} 
              className="legal-section"
              variants={itemVariants}
              whileHover="hover"
              initial="hidden"
              animate={controls}
            >
              <motion.h2
                whileHover={{ x: 5, color: "#2b6cb0" }}
              >
                {section.title}
              </motion.h2>
              <motion.div 
                className="legal-text"
                variants={containerVariants}
              >
                {section.content.map((paragraph, pIndex) => (
                  <motion.p 
                    key={pIndex}
                    variants={itemVariants}
                    whileHover={{ x: 3 }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
                {section.subsections && section.subsections.map((subsection, sIndex) => (
                  <motion.div key={sIndex} variants={containerVariants}>
                    <motion.h3
                      variants={itemVariants}
                      style={{color: '#0E7FF7'}}
                      // whileHover={{ x: 3, color: '#2c5282'}}
                    >
                      {subsection.title}
                    </motion.h3>
                    {subsection.content.map((paragraph, pIndex) => (
                      <motion.p 
                        key={pIndex}
                        variants={itemVariants}
                        whileHover={{ x: 3 }}
                      >
                        {paragraph}
                      </motion.p>
                    ))}
                  </motion.div>
                ))}
              </motion.div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div 
          className="legal-footer"
          variants={fadeInVariants}
        >
          <motion.p whileHover={{ scale: 1.01 }}>
            If you have any questions about our refund or cancellation policy, please contact us at support@ai<span style={{ position: 'relative', top: '-2px' }}>4</span>bazaar.com.
          </motion.p>
          <motion.p 
            className="copyright"
            whileHover={{ scale: 1.02 }}
          >
            © {new Date().getFullYear()} <b>VISYS CLOUD TECHNOLOGIES INDIA.</b> All rights reserved.
          </motion.p>
        </motion.div>
      </motion.div>
      <Footer />
    </div>
  );
};

export default RefundCancellation;